import {
  Row,
  Col,
  Heading,
  Button,
  Search,
  DataTable,
  Text,
  Spinner,
  Layout,
  Header,
  Badge,
} from 'elmo-elements';
import React, { useState } from 'react';
import {
  generatePath,
  Link,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Role } from '@/lib/auth/roles/role.enum';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { appData } from '@/lib/data';
import { getQueryString, pushParamsToUrl } from '@/lib/util';
import { useGetElearningCategoryQuery } from '@/modules/services';
import MyLibraryRoutes from '@/pages/MyLibrary/MyLibraryRoutes';
import { ContentManagerParamTypes } from '../ContentManagerRoutes';
import { format as formatDate } from 'date-fns';
import { DATEONLY_FORMAT } from '@/app/const';
import { ModuleSlideRoutes } from '@/pages/Module/routes';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CategoryModal from './CategoryModal';

const columns = [
  {
    title: 'Module',
    sortable: true,
    sortKey: 'name',
  },
];

const CategoryView = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const { learningElaCourseLibraryAdmin: isElaCourseLibraryEnabled } =
    useFlags();

  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );

  const isElmoAdmin = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);
  const [searchKeyword, setSearchKeyword] = useState<string>(
    currentQueryString?.search || '',
  );
  const [currentPage, setCurrentPage] = useState(currentQueryString.page || 1);
  const [order, setOrder] = useState<'asc' | 'desc'>(
    currentQueryString.order?.toLowerCase() || 'asc',
  );
  const [sortColumn, setSortColumn] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const limit = currentQueryString?.limit || 10;

  const { categoryId } = useParams<ContentManagerParamTypes>();

  const { data: categoryData, isLoading: isCategoryDataLoading } =
    useGetElearningCategoryQuery([categoryId, currentQueryString], {
      skip: !isElmoAdmin,
    });

  const handleSearch = (keyword: string) => {
    pushParamsToUrl(
      {
        search: keyword,
        page: 1,
      },
      history,
    );
  };

  const handleLimitChange = (newSize: number) => {
    pushParamsToUrl(
      {
        limit: newSize,
      },
      history,
    );
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);

    if (searchValue === '') handleSearch('');
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    pushParamsToUrl({ page: pageNumber }, history);
  };

  const handleColumnSort = (columnIndex: number) => {
    // handle column sort here.
    const sortKey = columns[columnIndex].sortable
      ? columns[columnIndex].sortKey
      : '';
    if (!sortKey) return;

    const newOrder =
      order === 'asc' && sortColumn === columnIndex ? 'desc' : 'asc';

    setOrder(newOrder);
    setSortColumn(columnIndex);

    pushParamsToUrl(
      {
        sort: sortKey,
        order: newOrder.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  if (
    (isElmoAdmin !== undefined && !isElmoAdmin) ||
    !isElaCourseLibraryEnabled
  ) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }
  if (isCategoryDataLoading || categoryData === undefined) return <Spinner />;

  return (
    <div id="category-view">
      <CategoryModal
        categoryData={categoryData}
        isModalOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(isSuccess) => {
          setIsModalOpen(!isSuccess);
        }}
      />
      <Layout.Header>
        <Header title={categoryData?.name}>
          <Row className="header-info">
            <Col span={10} className="category-info">
              <Badge
                type={categoryData?.isEnabled ? 'success' : 'grey'}
                className="categories-badge"
              >
                {categoryData?.isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              {categoryData?.createdAt && (
                <span>
                  <Text size="sm" className="category-date-label">
                    Date created:
                  </Text>
                  <Text size="sm">
                    {formatDate(
                      new Date(categoryData?.createdAt),
                      DATEONLY_FORMAT,
                    )}
                  </Text>
                </span>
              )}
              {categoryData?.modifiedAt && (
                <span>
                  <Text size="sm" className="category-date-label">
                    Last modified:
                  </Text>
                  <Text size="sm">
                    {formatDate(
                      new Date(categoryData?.modifiedAt),
                      DATEONLY_FORMAT,
                    )}
                  </Text>
                </span>
              )}
            </Col>
            <Col span={4} className="edit-category-btn">
              <Button
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                Edit
              </Button>
            </Col>
          </Row>
        </Header>
      </Layout.Header>
      <div className="editor-layout__body">
        <Layout.Content>
          <div id="Categories" className="content-manager">
            <Row className="categories-header categories-label-row">
              <Col span={10}>
                <Heading size="lg" htmlTag="h2" type="heading">
                  Modules with this category
                </Heading>
              </Col>
              <Col span={6} className="search">
                <Search
                  value={searchKeyword}
                  isVisible
                  onChange={(value) => handleSearchChange(value)}
                  onSubmit={() => handleSearch(searchKeyword)}
                  isSubmitEnabledAlways={true}
                />
              </Col>
            </Row>
            <DataTable
              className="content-manager-categories-table"
              isLoading={isCategoryDataLoading}
            >
              <DataTable.Header
                columns={columns}
                onSort={handleColumnSort}
                sortColumn={sortColumn}
                sortDirection={order}
              />
              <DataTable.Body>
                {categoryData?.elearnings?.map(
                  (elearning: { id: string; name: string }) => (
                    <DataTable.Tr key={elearning.id}>
                      <DataTable.Td>
                        <Link
                          to={generatePath(ModuleSlideRoutes.path, {
                            elearningId: elearning.id,
                          })}
                        >
                          {elearning.name}
                        </Link>
                      </DataTable.Td>
                    </DataTable.Tr>
                  ),
                )}
              </DataTable.Body>
              <DataTable.Pagination
                pageSize={categoryData?.meta.limit ?? limit}
                pageSizeOptions={[10, 20]}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handleLimitChange} // Note: page size isn't supported yet by the backend default size is set to 10.
                totalResults={categoryData?.meta.itemCount ?? 0}
              />
            </DataTable>
          </div>
        </Layout.Content>
      </div>
    </div>
  );
};

export default CategoryView;
